import React, { useMemo } from 'react';
import BixFileButton from '$/components/global/bixFileButton';
import BixButton from '$/components/global/bixButton';
import TargetBox from './targetBox';
import * as Bixby from '$/core';
import { v4 as uuidv4 } from 'uuid';
import FileList from './fileList';
import './index.scss';
import { IDocumentFile } from '$/core/models';
import dayjs from 'dayjs';

interface ICompanyDocumentFileUploadProps {
  companyDocumentCategories: Bixby.Models.CompanyDocumentCategory[];
  droppedFiles: Bixby.Models.IDocumentFile[];
  isProcessingFiles?: boolean;
  currentCompany?: Bixby.Models.AdminCompany;
  rejectWatermarks?: boolean;
  clientDataSiteProvider?: Bixby.Models.ClientDataSiteProvider | undefined;
  onSetDroppedFiles: (files: Bixby.Models.IDocumentFile[]) => void;
  onPreValidateFiles?: (files: Bixby.Models.IDocumentFile[]) => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
  onDownloadDocument: (id: string) => Promise<void>;
}

const CompanyDocumentFileUpload: React.FC<ICompanyDocumentFileUploadProps> = props => {
  const {
    currentCompany,
    companyDocumentCategories,
    droppedFiles,
    onSetDroppedFiles,
    rejectWatermarks,
    clientDataSiteProvider,
    isProcessingFiles,
    dispatchToastError,
    onPreValidateFiles,
    onDownloadDocument,
  } = props;

  const validateFileNamesAndSetFiles = (files: IDocumentFile[], shouldRunPrevalidation: boolean) => {
    // validate filenames, find duplicates
    const mapped = files.map(file => {
      const model: IDocumentFile = {
        ...file,
        id: file.id || uuidv4(),
        fileName: file.fileName || file.file.name || '',
        title: file.title || file.file.name.replace(/\.[^/.]+$/, ''), // default to file without extension
        hasDuplicateNameValidationError:
          files.filter(_ => _.fileName === file.fileName).length > 1 &&
          files.filter(_ => _.file.name === file.file.name).length > 1,
        rowNumber: file.rowNumber || undefined,
        documentDeclarationType: file.documentDeclarationType || undefined,
      };

      return model;
    });
    if (shouldRunPrevalidation && onPreValidateFiles) {
      onPreValidateFiles([...mapped]);
    } else {
      onSetDroppedFiles([...mapped]);
    }
  };

  const handleFileDrop = (item: { files: any[] }) => {
    if (item) {
      const mapped = Array.from(item.files).map(file => {
        const model: IDocumentFile = {
          id: uuidv4(),
          file: file,
          fileName: file.name || '',
          title: file.name.replace(/\.[^/.]+$/, ''), // default to file without extension
          rowNumber: file.rowNumber || undefined,
          documentDeclarationType: file.documentDeclarationType || undefined,
        };
        return model;
      });

      validateFileNamesAndSetFiles(mapped, true);
    }
  };

  const handleDeleteFile = (id: string) => {
    const foundIndex = droppedFiles.findIndex(_ => _.id === id);

    if (foundIndex > -1) {
      droppedFiles.splice(foundIndex, 1);
      validateFileNamesAndSetFiles([...droppedFiles], false);
    }
  };

  const handleManualFileUpload = (event: any) => {
    const model: { files: any[] } = { files: event.target.files };

    // prevent users from uploading multiple zip files at the same time
    if (
      droppedFiles.some(_ => _.submissionType === Bixby.Enums.CompanyDocumentSubmissionType.Zip) &&
      Array.from(model.files).some(_ => _.name.endsWith('.zip'))
    ) {
      dispatchToastError('You cannot upload multiple zip files at the same time.');
      return;
    }

    handleFileDrop(model);
  };

  const handleSetMetadata = (id: string, key: keyof Bixby.Models.IDocumentFile, value: any) => {
    const found = droppedFiles.find(_ => _.id === id);
    if (!found) return;
    if (found[key] === 'lastModified' || found[key] === 'name' || found[key] === 'size' || found[key] === 'type')
      return;

    (found[key] as any) = value;
    // submit becomes disabled if hasServerValidationError !== false
    found.hasServerValidationError = undefined;

    onSetDroppedFiles([...droppedFiles]);
  };

  const uploadButtonDisabled = useMemo(
    () =>
      isProcessingFiles ||
      ((rejectWatermarks ? currentCompany?.isWatermarkLocked : currentCompany?.isWatermarkLocked) &&
        !clientDataSiteProvider),
    [isProcessingFiles, rejectWatermarks, currentCompany, clientDataSiteProvider]
  );
  return (
    <>
      <div className="upload-button-container">
        <BixFileButton
          onChange={handleManualFileUpload}
          multiple
          useSmall
          disabled={uploadButtonDisabled}
          useLoading={isProcessingFiles !== undefined}
          isLoading={isProcessingFiles}>
          Upload Files
        </BixFileButton>
        <BixButton
          type="button"
          useSmall
          light
          onClick={() => {
            onSetDroppedFiles([]);
          }}
          disabled={droppedFiles && droppedFiles.length <= 0}>
          Clear All Files
        </BixButton>
      </div>
      {currentCompany && currentCompany?.fiscalYearEnd && (
        <p>Fiscal Year End: {dayjs.utc(currentCompany?.fiscalYearEnd).format('MMM')} </p>
      )}
      {droppedFiles &&
        droppedFiles.length > 0 &&
        (droppedFiles.some(_ => _.submissionType === Bixby.Enums.CompanyDocumentSubmissionType.Zip) &&
        droppedFiles.some(_ => _.submissionType === Bixby.Enums.CompanyDocumentSubmissionType.Files) ? (
          <p>
            <i>Viewing files from both {droppedFiles[0].file.name} and individual files.</i>
          </p>
        ) : (
          <p>
            <i>Viewing files from {droppedFiles[0].file.name}.</i>
          </p>
        ))}
      <TargetBox
        onDrop={handleFileDrop}
        hasFiles={droppedFiles && droppedFiles.length > 0}
        disabled={uploadButtonDisabled ?? true}>
        {droppedFiles && droppedFiles.length > 0 ? (
          <FileList
            files={droppedFiles}
            onDownloadDocument={onDownloadDocument}
            onDeleteFile={handleDeleteFile}
            companyDocumentCategories={companyDocumentCategories}
            onSetMetadata={handleSetMetadata}
            fiscalYearEnd={currentCompany?.fiscalYearEnd}
          />
        ) : (
          <p>Drag files here</p>
        )}
      </TargetBox>
    </>
  );
};

export default CompanyDocumentFileUpload;
